<template>
    <div ref="dropdown" v-click-outside="closeDropdown" :class="['custom-select-container', theme]">
        <label v-if="label !== ''" class="custom_label label_margin">{{ label }}</label>
        
        <div class="custom-dropdown">
            <!-- Etiqueta principal -->
            <div class="dropdown_label_select selected_label is-flex is-justify-content-flex-start"
                @click="toggleDropdown"
                :class="{ 
                    'dropdown_label_select_clicked': isOpen && !hasSelectedCheckboxes, 
                    'dropdown_background_selected_items': hasSelectedCheckboxes 
                }">
                
                <span v-if="currentIcon !== ''" class="iconDefault">
                    <component :is="getCurrentIcon" class="svgIcon" />
                </span>
                
                <span v-if="staticLabel !== ''" class="ml-1">
                    {{ staticLabel }}
                </span>
                <span v-else class="ml-1">{{ selectedLabel }}</span>
                
                <span v-if="iconRight != ''" class="ml-1 iconDefault"
                    :class="{ 'iconWhite': hasSelectedCheckboxes }">
                    <component :is="iconRight" class="svgIcon" />
                </span>
            </div>

            <!-- Menú desplegable -->
            <transition name="slide">
                <div v-if="isOpen" 
                     class="dropdown_menu_select" 
                     :class="theme === 'classification_theme' ? 'dropdown_wide' : 'dropdown_normal'">
                     
                    <!-- Input de búsqueda añadido -->
                    <div v-if="hasSearchBox" class="search-container">
                        <input 
                            type="text" 
                            ref="searchInput" 
                            v-model="searchQuery" 
                            class="dropdown_search_input" 
                            placeholder="Buscar..."
                            @click.stop
                        />
                    </div>
                    
                    <ul class="list_list">
                        <!-- Estilo: Classification Theme -->
                        <template v-if="theme === 'classification_theme'">
                            <template v-for="(option, index) in filteredData">
                                <!-- Separador / Nivel 2 -->
                                <li v-if="option.separator" 
                                    :key="'sep_' + index" 
                                    class="dropdown_item dropdown_title">
                                    <b-checkbox v-if="isSearchable" 
                                        :value="checkboxSelected['2_' + option.level_2]"
                                        @input="handleCheckboxChange('2_' + option.level_2, index, $event)">
                                        {{ option.level_2 }}
                                    </b-checkbox>
                                    <span v-else>{{ option.level_2 }}</span>
                                </li>
                                
                                <!-- Elemento / Nivel 3 -->
                                <li v-else 
                                    :key="'opt_' + option.value + '_' + index" 
                                    class="dropdown_item dropdown_subitem"
                                    :class="{ 'selected-item-menu': checkboxSelected['3_' + option.label] }"
                                    @click.stop="selectOption(option)">
                                    <b-checkbox v-if="isSearchable" 
                                        :value="checkboxSelected['3_' + option.label]"
                                        @input="handleCheckboxChange('3_' + option.label, index, $event)">
                                        {{ option.label }}
                                    </b-checkbox>
                                    <span v-else>{{ option.label }}</span>
                                </li>
                            </template>
                        </template>

                        <!-- Estilo: Normal / Por defecto -->
                        <template v-else>
                            <li v-for="(option, index) in filteredData" 
                                :key="'norm_' + (option.value || index)"
                                class="dropdown_item" 
                                :class="{ 'selected-item-menu': option.value === value }"
                                @click.stop="selectOption(option)">
                                {{ option.label }}
                            </li>
                        </template>
                    </ul>
                </div>
            </transition>
        </div>
    </div>
</template>

<script>

import ArrowDown from "@/components/svg/ArrowDown.vue";
import Calendar from "@/components/svg/Calendar.vue";
import RobotFace from "@/components/svg/RobotFace.vue";
import UploadFile from "@/components/svg/UploadFile.vue";
import Layers from "@/components/svg/Layers.vue";
import DashboardSquare from "@/components/svg/DashboardSquare.vue"
import clickOutside from "@/directives/clickOutside";
export default {
    name: "SelectComponent",
    components: {
        ArrowDown,
        Calendar,
        RobotFace,
        UploadFile,
        Layers,
        DashboardSquare
    },
    directives: { clickOutside },
    props: {
        options: {
            type: Array,
            required: false,
            default: () => [],
        },
        weirdOptions: {
            type: Array,
            required: false,
            default: () => [],
        },
        value: {
            type: [String, Number, Object],
            default: null,
        },
        label: {
            type: String,
            default: '',
        },
        currentIcon: {
            type: String,
            default: '',
        },
        theme: {
            type: String,
            default: 'default_theme'
        },
        isAccount: {
            type: Boolean,
            default: false,
        },
        isSearchable: {
            type: Boolean,
            default: false,
        },
        staticLabel: {
            type: String,
            default: ''
        },
        hasSearchBox: {
            type: Boolean,
            default: false
        },
        iconRight: {
            type: String,
            default: ''
        },
        // Multi-select: labels (strings) que vienen tildados al montar/cambiar
        // el prop. Convención: cada label se guarda como key `3_<label>` en
        // `checkboxSelected` para alinear con `getExportListCheckbox`. Opt-in,
        // los call sites existentes (single-select sin preselección) no se
        // ven afectados.
        initialSelectedValues: {
            type: Array,
            default: () => [],
        },
    },
    data() {
        return {
            isOpen: false,
            data: [],
            checkboxSelected: {},
            searchQuery: ''
        };
    },
    computed: {
        getCurrentIcon() {
            return this.currentIcon === 'calendar' ? 'Calendar' : this.currentIcon;
        },
        hasSelectedCheckboxes() {
            return this.getExportListCheckbox().length > 0;
        },
        selectedLabel() {
            const selected = this.data.find(option => option.value === this.value);
            if (selected) {
                return selected.label;
            }
            return this.theme === 'classification_theme' ? 'Sin Clasificar' : 'Seleccionar';
        },
        filteredData() {
            if (!this.searchQuery) return this.data;
            
            const normalizeString = (str) => {
                return str.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
            };
            
            const query = normalizeString(this.searchQuery);
            const result = [];
            let currentSeparator = null;

            for (const item of this.data) {
                if (item.separator) {
                    currentSeparator = item;
                } else if (item.label && normalizeString(item.label).includes(query)) {
                    if (currentSeparator && !result.includes(currentSeparator)) {
                        result.push(currentSeparator);
                    }
                    result.push(item);
                }
            }
            return result;
        }
    },
    methods: {
        handleCheckboxChange(key, index, isChecked) {
            const [level, text] = key.split('_');
            const updates = { [key]: isChecked };

            // Si es nivel 2 (grupo), propagar a todos sus hijos (nivel 3)
            if (level === '2') {
                for (let i = index + 1; i < this.data.length; i++) {
                    const item = this.data[i];
                    if (item.level_2 !== text) break;
                    updates['3_' + item.label] = isChecked;
                }
            }

            // Reasignación inmutable en lugar de this.$set por key — funciona
            // en Vue 2 + 3 sin depender del observer dinámico de Vue 2.
            this.checkboxSelected = { ...this.checkboxSelected, ...updates };
            this.emitSelectedValues();
        },
        toggleDropdown() {
            this.isOpen = !this.isOpen;
            if (this.isOpen) {
                this.searchQuery = '';
                this.$nextTick(() => {
                    if (this.$refs.searchInput) {
                        this.$refs.searchInput.focus();
                    }
                });
            }
        },
        selectOption(option) {
            this.$emit('input', option.value);
            if (!this.isSearchable) {
                this.isOpen = false;
            }
        },
        closeDropdown() {
            this.isOpen = false;
        },
        parseWeirdOptions(rawOptions) {
            const options = Object.values(rawOptions).map(option => ({
                separator: false,
                value: option.id,
                label: this.isAccount ? option.level_3 : option.name,
                level_1: option.level_1,
                level_2: option.level_2
            }));

            if (!this.isAccount) {
                return options;
            }

            const finalOptions = [];
            let lastLevel2 = '';

            for (const option of options) {
                if (lastLevel2 !== option.level_2) {
                    lastLevel2 = option.level_2;
                    finalOptions.push({ separator: true, level_2: lastLevel2 });
                }
                finalOptions.push(option);
            }
            return finalOptions;
        },
        getExportListCheckbox() {
            return Object.entries(this.checkboxSelected)
                .filter(([key, isSelected]) => isSelected && key.startsWith('3_'))
                .map(([key]) => key.split('_')[1]);
        },
        emitSelectedValues() {
            this.$emit("selected-values", this.getExportListCheckbox());
        },
        clearCbList() {
            this.checkboxSelected = {};
            this.emitSelectedValues();
        },
        // Rehidrata el estado de los checkboxes desde `initialSelectedValues`.
        // No emite `selectedValues` porque la fuente de verdad es el padre que
        // pasa el prop — emitir crearía un ciclo cuando el padre escucha
        // `selectedValues` para actualizar el mismo store que alimenta el prop.
        applyInitialSelectedValues() {
            const next = {};
            (this.initialSelectedValues || []).forEach((label) => {
                next["3_" + label] = true;
            });
            this.checkboxSelected = next;
        },
        deleteItemCbList(itemLabel) {
            const key = Object.keys(this.checkboxSelected).find(k => k.includes(itemLabel));
            if (key) {
                this.checkboxSelected = { ...this.checkboxSelected, [key]: false };
                this.emitSelectedValues();
            }
        }
    },
    watch: {
        // El watcher es immediate → corre en created() y ya inicializa `data`.
        // Antes había un `mounted()` haciendo lo mismo + un setTimeout(5ms)
        // adentro del handler para que el watcher ganara la carrera contra el
        // mounted; con el mounted removido el setTimeout deja de ser necesario.
        weirdOptions: {
            handler(newVal) {
                if (newVal && newVal.length > 0) {
                    this.data = this.parseWeirdOptions(newVal);
                } else {
                    this.data = this.options;
                }
            },
            immediate: true,
        },
        options: {
            handler(newVal) {
                if (!this.weirdOptions || this.weirdOptions.length === 0) {
                    this.data = newVal;
                }
            }
        },
        initialSelectedValues: {
            handler() {
                this.applyInitialSelectedValues();
            },
            immediate: true,
        },
    },
};
</script>
<style lang="scss" scoped>
.default_theme {
    --background-color: #002e33;
    --background-hover: #185555;
    --background-click: #3b7a7a;
    --background-click-hover: #3b7a7a;
    --text-color-selected-items: #101828;
    --text-color: white;
    --color-border: #dcdcdc;
    --padding-vert: 12px;
    --padding-horz: 16px;
    --height: 46px;
}

.classification_theme {
    --background-color: white;
    --background-color-selected-items: #002e33;
    --text-color-selected-items: #101828;
    --background-hover: #185555;
    --background-click: #fffffa;
    --background-click-hover: #f8f8f8;
    --text-color: #333a48;
    --text-color-on-selected: white;
    --color-border: #dcdcdc;
    --padding-vert: 2px;
    --padding-horz: 8px;
    --height: 46px;
    text-transform: capitalize;
}

.report_theme {
    --background-color: white;
    --background-color-selected-items: #002e33;
    --text-color-selected-items: #00A9AD;
    --background-hover: #fcfcfa;
    --background-click: #fffffa;
    --background-click-hover: #f8f8f8;
    --text-color: #5C5C5C;
    --text-color-on-selected: #5C5C5C;
    --color-border: #EBEBEB;
    --padding-vert: 2px;
    --padding-horz: 8px;
    --height: 30px;
    text-transform: capitalize;
}

.custom-select-container {
    position: relative;
}


.custom_label {
    font-size: 0.9rem;
    color: #4a4a4a;
    display: block;
}

.dropdown_label_select {
    background-color: var(--background-color);
    color: var(--text-color);
    border: 1px solid var(--color-border);
    border-radius: 8px;
    cursor: pointer;
    display: flex;
    justify-content: space-between;
    align-items: center;
    box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.1);
    width: 100%;
    padding: var(--padding-vert) var(--padding-horz);
    // color: var(--text-color);
    user-select: none;

    font-size: 16px;
    font-style: normal;
    font-weight: 600;
    line-height: 24px;
    height: var(--height);
}

.dropdown_background_selected_items {
    background-color: var(--background-color-selected-items);
    color: var(--text-color-on-selected);
}

.iconWhite {
    filter: brightness(0) invert(1);
}

$select_transition: 0.1s;

.dropdown_label_select:hover {
    background-color: var(--background-hover) !important;
    transition: all $select_transition ease;
    color: var(--text-color-on-selected, --text-color);

    .iconDefault {
        filter: brightness(0) invert(1);
    }
}

.dropdown_label_select_clicked {
    background-color: var(--background-click);
    color: var(--text-color);
}

.dropdown_label_select_clicked:hover {
    background-color: var(--background-click-hover) !important;
    color: var(--text-color);
    transition: all $select_transition ease;

    .iconDefault {
        filter: none;
    }
}

.label_margin {
    margin-bottom: 0.5rem;
}

.actual_button_arrow_down {
    flex-shrink: 0;
    -ms-flex-align: end;
    align-self: center;
}

.custom-dropdown {
    position: relative;
    width: 100%;
    // max-width: 300px;
    color: #fff;
}


.selected_label {
    line-height: 1;
    font-size: 14px;
}

.svgIcon {
    width: 20px;
    height: 20px;
}

.dropdown-arrow {
    font-size: 0.8rem;
    color: #b5b5b5;
}

.dropdown_normal {
    width: 100%;
}

.dropdown_wide {
    width: 300px;
    max-height: 400px;
    overflow-y: scroll;
    overflow-x: hidden;
    z-index: 4;
}

.label {
    width: 100%;
}

.dropdown_menu_select {
    position: absolute;
    top: 99%;
    left: 0;
    background-color: #ffffff;
    border: 1px solid #dcdcdc;
    border-radius: 8px;
    box-shadow: 0px 4px 8px rgba(0, 0, 0, 0.1);
    z-index: 100;
    display: flex;
    flex-direction: column;
    padding: 2px;
}

.dropdown_item {
    padding: 0.5rem;
    cursor: pointer;
    color: var(--Gray-700, #344054);
    width: 100%;
    font-size: 14px;
    font-style: normal;
    font-weight: 500;
    line-height: 20px;
    user-select: none;
}

.dropdown_item:hover {
    background-color: #f5f5f5;
    border-radius: 8px;
}

.selected-item-menu {
    font-weight: 600;
    color: var(--text-color-selected-items);
    line-height: 20px;
}

.slide-enter-active,
.slide-leave-active {
    transition: max-height 0.05s ease;
}

.slide-enter {
    max-height: 0px;
}

.slide-leave-to {
    max-height: 0;
}

.dropdown_title {
    font-weight: 900;
    border-bottom: 1px solid #475467;
}

.dropdown_subitem {
    padding-left: 28px;
}

.list_list {
    width: 100%;
}

.search-container {
    padding: 8px;
    border-bottom: 1px solid var(--color-border, #dcdcdc);
    width: 100%;
}

.dropdown_search_input {
    width: 100%;
    padding: 6px 12px;
    border: 1px solid #dcdcdc;
    border-radius: 4px;
    outline: none;
    font-size: 14px;
    color: var(--Gray-700, #344054);
    box-sizing: border-box;
}

.dropdown_search_input:focus {
    border-color: #00A9AD;
}
</style>